import {
  SubContent as RadixSubContent,
  Portal as RadixPortal,
} from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import {
  forwardRef,
  useCallback,
  useContext,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

import { useOverlayStacking } from '../../core/hooks/useOverlayStacking.js';
import { AriaLabelingProps } from '../../core/types/a11y-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { MenuPortalOwnerContext } from './contexts/MenuPortalOwnerContext.js';
import * as styles from './Menu.sty.js';

/**
 * @public
 */
export interface MenuSubContentProps
  extends
    WithChildren,
    DataTestId,
    MaskingProps,
    StylingProps,
    AriaLabelingProps {}

type InternalMenuSubContentProps = MenuSubContentProps & {
  /** @internal */
  _onTabKeyPressed: () => void;
  /** @internal */
  'data-dt-component'?: string;
};

const SUB_CONTENT_SIDE_OFFSET = 0; // NOTE: This effectively results in an offset of -4px due to the default behavior of radix-ui sub-menus

/**
 * @internal
 */
export const ALIGN_OFFSET = -4;

/**
 * The component that pops out when the dropdown menu is open.
 * @public
 */
export const SubContent = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  MenuSubContentProps
>((props, forwardedRef) => {
  const {
    _onTabKeyPressed,
    children,
    className: consumerClassName,
    style: consumerStyle,
    ...remainingProps
  } = props as InternalMenuSubContentProps;
  const tabKeyHandler = useCallback(
    (e: ReactKeyboardEvent) => {
      if (_onTabKeyPressed && e.key === 'Tab') {
        _onTabKeyPressed();
      }
    },
    [_onTabKeyPressed],
  );
  const { style } = useOverlayStacking(undefined, 'overlay');
  const ownerId = useContext(MenuPortalOwnerContext);

  return (
    <RadixPortal>
      <RadixSubContent
        {...remainingProps}
        ref={forwardedRef}
        sideOffset={SUB_CONTENT_SIDE_OFFSET}
        alignOffset={ALIGN_OFFSET}
        onKeyUp={tabKeyHandler}
        className={clsx(
          styles.content,
          styles.groupContainer,
          consumerClassName,
        )}
        style={{ ...consumerStyle, zIndex: style.zIndex }}
        collisionPadding={2}
        {...(ownerId ? { 'data-tooltip-menu-id': ownerId } : {})}
      >
        {children}
      </RadixSubContent>
    </RadixPortal>
  );
});

(SubContent as typeof SubContent & { displayName: string }).displayName =
  'Menu.SubContent';
